
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { debounce } from "typescript-debounce-decorator";
import httpClient from "../../store/httpClient";
import { InvoiceManagementDto } from "@/dtos/invoiceManagementDto";
import { InvoiceSorting } from "@/dtos/invoiceSorting";
import { ErpInvoiceState } from "@/dtos/erpInvoiceState";
import { InvoiceSearchRequestDto } from "@/dtos/invoiceSearchRequestDto";
import DatePicker from "@/components/DatePicker.vue";
import CreateMahnungDialog from "./CreateMahnungDialog.vue";

@Component({
	components: {
		DatePicker,
		CreateMahnungDialog,
	},
})
export default class AllInvoices extends Vue {
	@Prop()
	private isLoading?: boolean;
	@Prop()
	private hasMoreToLoad?: boolean;
	@Prop()
	private loadMoreInvoicesToggle?: boolean;
	private loadedInvoicesUpTo = 0;
	private loadBatchSize = 50;
	private filterRegions = [];
	private onlyShowOpen = false;
	private onlyShowGemahnt: boolean = false;
	private onlyPrepayInvoices: boolean = false;
	private currentInvoiceId: number = 0;
	private currentInvoiceDueDate: Date = new Date();
	private mahnungNotDueInvoiceDialog: boolean = false;
	private allStates: Array<ErpInvoiceState> = [
		ErpInvoiceState.Unvollstaendig,
		ErpInvoiceState.Gueltig,
		ErpInvoiceState.Bezahlt,
		ErpInvoiceState.Storniert,
	];
	private dueUntilDate: Date = new Date();
	private headers: Array<{
		text: string;
		value: string;
		align: string;
		sortable: boolean;
		width: string;
	}> = Array(
		{
			text: "Rechnungs-Id",
			value: "invoiceId",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Kunden-Id",
			value: "customerId",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Auftrag-Id",
			value: "orderId",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Fällig",
			value: "dueDate",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Erstellt",
			value: "createdAt",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Rechnungskontakt",
			value: "invoiceAddress",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Betrag",
			value: "totalAmount",
			align: "right",
			sortable: true,
			width: "1px",
		},
		{
			text: "Status",
			value: "state",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Gemahnt",
			value: "mahnung",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "",
			value: "menu",
			align: "right",
			sortable: false,
			width: "0px",
		}
	);

	private customSort(items: any, index: any, isDesc: any) {
		// Do nothing because items are correclty sorted by the backend
		// But a custom sort is needed because otherwise the data-table shuffles items around
		// Maybe there is another solution to stop the sorting but I haven't found it, yet.
		return items;
	}

	private mounted() {
		this.filterRegions = this.regions;
		if (this.invoices.length === 0) {
			this.loadInvoices(true);
		}
	}

	@Watch("loadMoreInvoicesToggle")
	private async loadMoreInvoices() {
		if (this.loadMoreInvoicesToggle && !this.isLoading) {
			this.loadInvoices(false);
		}
		this.$emit("resetLoadMoreToggle");
	}

	@Watch("dueUntilDate")
	private async reloadDueDate() {
		this.loadInvoices(true);
	}

	private get filteredStates() {
		return this.onlyShowOpen
			? [ErpInvoiceState.Gueltig, ErpInvoiceState.Unvollstaendig]
			: this.allStates;
	}

	private get invoices() {
		return this.$store.state.invoices;
	}
	private set invoices(newInvoices: InvoiceManagementDto[]) {
		this.$store.commit("setInvoices", newInvoices);
	}

	private get searchTerm(): string {
		return this.$store.state.invoiceSearchTerm;
	}
	private set searchTerm(searchTerm: string) {
		this.$store.commit("setInvoiceSearchTerm", searchTerm);
	}
	private get lastSearchTerm(): string {
		return this.$store.state.invoiceLastSearchTerm;
	}
	private set lastSearchTerm(lastSearchTerm: string) {
		this.$store.commit("setInvoiceLastSearchTerm", lastSearchTerm);
	}

	private get customerNumber(): string {
		return this.$store.state.invoiceCustomerNumber;
	}
	private set customerNumber(customerNumber: string) {
		this.$store.commit("setInvoiceCustomerNumber", customerNumber);
	}
	private get lastCustomerNumber(): string {
		return this.$store.state.invoiceLastCustomerNumber;
	}
	private set lastCustomerNumber(customerNumber: string) {
		this.$store.commit("setInvoiceLastCustomerNumber", customerNumber);
	}

	private get invoiceSorting() {
		return this.$store.state.invoiceSortingType;
	}
	private set invoiceSorting(invoiceSorting: any) {
		this.$store.commit("setInvoiceSortingType", invoiceSorting);
	}

	private get regions() {
		return this.$store.state.regions;
	}

	private get totalInvoices() {
		return this.invoices.length;
	}
	private get totalAmountInvoicesChf() {
		return this.invoices
			.filter((i: InvoiceManagementDto) => i.currency === "CHF")
			.reduce((acc: number, i: InvoiceManagementDto) => {
				return acc + (i.totalAmount - i.paidAmount);
			}, 0);
	}
	private get totalAmountInvoicesEur() {
		return this.invoices
			.filter((i: InvoiceManagementDto) => i.currency === "EUR")
			.reduce((acc: number, i: InvoiceManagementDto) => {
				return acc + (i.totalAmount - i.paidAmount);
			}, 0);
	}

	private async loadInvoices(reload: boolean) {
		if (this.isLoading) {
			return;
		}
		try {
			this.$emit("setIsLoading", true);
			if (reload) {
				this.loadedInvoicesUpTo = 0;
			}
			let searchRequest: InvoiceSearchRequestDto = {
				searchTerm: this.searchTerm,
				invoiceIds: new Array<number>(),
				states: this.filteredStates,
				sortingType: this.invoiceSorting,
				regions: this.filterRegions,
				onlyShowGemahnt: this.onlyShowGemahnt,
				dueUntil: this.dueUntilDate,
				customerNumber: this.customerNumber,
				onlyShowPrepay: this.onlyPrepayInvoices,
			};
			let rawInvoices: InvoiceManagementDto[] = [];
			if (this.onlyShowOpen) {
				const response = await httpClient().post(
					"Invoice/GetInvoices?from=0&to=0",
					searchRequest
				);
				rawInvoices = response.data;
				this.$emit("setHasMoreToLoad", false);
				this.loadedInvoicesUpTo = rawInvoices.length;
			} else {
				const response = await httpClient().post(
					"Invoice/GetInvoices?" +
						`from=${this.loadedInvoicesUpTo}&` +
						`to=${this.loadedInvoicesUpTo + this.loadBatchSize}`,
					searchRequest
				);
				rawInvoices = response.data;
				this.$emit("setHasMoreToLoad", rawInvoices.length === this.loadBatchSize);
				this.loadedInvoicesUpTo += rawInvoices.length;
			}
			rawInvoices.forEach((i: any) => {
				i.mahnungCost = 0.0;
			});

			if (reload) {
				this.invoices = [];
			}
			this.$store.commit("addOrUpdateInvoices", rawInvoices);
		} catch (e) {
			console.log("Loading invoices failed.", e);
		} finally {
			this.$emit("setIsLoading", false);
		}
	}

	@debounce(700, { leading: false })
	@Watch("searchTerm")
	private onSearchTermChange() {
		this.reload();
	}
	@debounce(700, { leading: false })
	@Watch("customerNumber")
	private onCustomerNumberChange() {
		this.reload();
	}

	private reload() {
		if (
			!this.isLoading &&
			(this.lastSearchTerm !== this.searchTerm ||
				this.lastCustomerNumber !== this.customerNumber) &&
			(this.searchTerm.length >= 3 || this.searchTerm.length === 0)
		) {
			this.lastSearchTerm = this.searchTerm;
			this.lastCustomerNumber = this.customerNumber;
			this.loadInvoices(true);
		}
	}

	private changeSort(column: string, sortable: boolean) {
		if (sortable) {
			let newSort: InvoiceSorting;
			if (this.sortProps.sortByColumn === column) {
				newSort = this.sortProps.sortDescending
					? this.invoiceSorting - 1
					: this.invoiceSorting + 1;
			} else {
				newSort = this.getSortTypeFromColumn(column);
			}
			this.$store.commit("setInvoiceSorting", newSort);
			this.loadInvoices(true);
		}
	}

	private async checkCreateMahnung(invoiceId: number, dueDate: Date) {
		this.currentInvoiceId = invoiceId;
		this.currentInvoiceDueDate = dueDate;
		this.mahnungNotDueInvoiceDialog = true;
	}

	private async savedMahnung() {
		this.$emit("reloadMahnungen");
		this.mahnungNotDueInvoiceDialog = false;
		await this.loadInvoices(true);
	}

	private manuallyLoadMoreInvoices() {
		if (!this.isLoading) {
			this.loadInvoices(false);
		}
		this.$emit("resetLoadMoreToggle");
	}

	private navigateToPayments(invoideId: number) {
		this.$emit("navigateToPayments", invoideId);
	}

	private async navigateToInvoiceEdit(invoiceId: number) {
		this.$router.push({ name: "editInvoice", params: { invoiceId: invoiceId.toString() } });
	}

	private goToCustomer(customerId: string) {
		this.$router.push({ name: "customerDetail", params: { customerId } });
	}

	private get sortProps() {
		let descending: boolean = false;
		let column: string = "createdAt";
		let currentSortingType: InvoiceSorting = this.$store.state.invoiceSortingType;
		switch (currentSortingType) {
			case InvoiceSorting.CreatedAtAscending: {
				descending = false;
				column = "createdAt";
				break;
			}
			case InvoiceSorting.CreatedAtDescending: {
				descending = true;
				column = "createdAt";
				break;
			}
			case InvoiceSorting.DueDateAscending: {
				descending = false;
				column = "dueDate";
				break;
			}
			case InvoiceSorting.DueDateDescending: {
				descending = true;
				column = "dueDate";
				break;
			}
			case InvoiceSorting.InvoiceTotalAscending: {
				descending = false;
				column = "totalAmount";
				break;
			}
			case InvoiceSorting.InvoiceTotalDescending: {
				descending = true;
				column = "totalAmount";
				break;
			}
			case InvoiceSorting.StateAscending: {
				descending = false;
				column = "state";
				break;
			}
			case InvoiceSorting.StateDescending: {
				descending = true;
				column = "state";
				break;
			}
			case InvoiceSorting.AddressAscending: {
				descending = false;
				column = "invoiceAddress";
				break;
			}
			case InvoiceSorting.AddressDescending: {
				descending = true;
				column = "invoiceAddress";
				break;
			}
		}
		return {
			sortDescending: descending,
			sortByColumn: column,
		};
	}

	private getSortTypeFromColumn(column: string) {
		switch (column) {
			case "dueDate": {
				return InvoiceSorting.DueDateDescending;
			}
			case "createdAt": {
				return InvoiceSorting.CreatedAtDescending;
			}
			case "totalAmount": {
				return InvoiceSorting.InvoiceTotalDescending;
			}
			case "state": {
				return InvoiceSorting.StateDescending;
			}
			case "invoiceAddress": {
				return InvoiceSorting.AddressDescending;
			}
			default: {
				return InvoiceSorting.CreatedAtDescending;
			}
		}
	}

	private getStateText(enumValue: number) {
		switch (enumValue) {
			case 10: {
				return "Unvollständig";
			}
			case 20: {
				return "Gültig";
			}
			case 30: {
				return "Bezahlt";
			}
			case 40: {
				return "Storniert";
			}
			default: {
				return "N/A";
			}
		}
	}
}
